import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    CircularProgress,
    Alert,
    IconButton,
    Button,
    Grid,
    Breadcrumbs,
    Container,
    Tooltip,
    Link as MuiLink
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DataGrid, GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { ExpandMore, ChevronRight, Download, InfoOutlined } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { reportAPI } from '../../services/reportAPI';
import { formatDateForInput, formatDate as formatDateDisplay } from '../../utils/dateUtils';

interface RevenueBreakdownItem {
    category: string;
    subcategory?: string;
    amount: number;
    tax: number;
    totalAmount: number;
    refunds: number;
    outstanding: number;
    /** Explicit columns from the backend (credit = collected, debit = refunded, net = credit − debit) */
    credit?: number;
    debit?: number;
    net?: number;
    /** HST reversed by refunds in the period */
    refundTax?: number;
    isSubcategory?: boolean;
    level?: number;
    isLiability?: boolean;
    /** Level-2 amenity rows: the amenity type (level-1 row) they belong to */
    groupSubcategory?: string;
}

// Maps the flat, type-based revenue categories (no tier/amenity breakdown) to the
// TransactionType enum value the backend drill-down endpoint expects.
const FLAT_CATEGORY_TYPE_MAP: Record<string, string> = {
    'Initiation Fees': 'initiation_fee',
    'Improvement Fees': 'improvement_fee',
    'Add-On Payments': 'add_on_payment',
    'Guest Fees': 'guest_fee',
    'Other': 'other',
    'Coaching Sessions': 'coaching',
    'Group Plays': 'group_play_booking',
    'Fitness Classes': 'class_booking',
    'Personal Training Sessions': 'personal_training',
    'Personal Training Packs': 'personal_training_pack',
    'Equipment Fees': 'equipment_fee'
};

interface DrillDownRequest {
    source: string;
    transactionType?: string;
    tierName?: string;
    amenityType?: string;
    amenityName?: string;
    paymentMethod?: string;
    label: string;
}

// Shop / F&B sections are sourced from the sales (all payment methods); their level-1 rows are
// payment methods, labelled by the backend's POS_METHOD_LABELS.
const POS_SALES_SOURCES: Record<string, string> = {
    'Food & Beverage': 'fnb_sales',
    'Pro Shop': 'shop_sales'
};
const POS_METHOD_BY_LABEL: Record<string, string> = {
    'Card (Terminal)': 'helcim',
    'Cash': 'cash',
    'Member Account': 'member_account'
};
const POS_METHOD_LABELS: Record<string, string> = {
    helcim: 'Card (Terminal)',
    cash: 'Cash',
    member_account: 'Member Account'
};

/** Column values for a row — explicit from the backend, with the older derivation as a fallback */
const rowColumns = (item: RevenueBreakdownItem) => ({
    credit: item.credit ?? (item.isLiability ? 0 : item.amount + Math.abs(item.refunds) + item.tax),
    debit: item.debit ?? item.refunds,
    taxReversed: item.refundTax ?? 0,
    net: item.net ?? (item.isLiability ? item.amount : item.amount + item.tax)
});

/** The level-1 row a level-2 row sits under */
const isChildOf = (child: RevenueBreakdownItem, parent: RevenueBreakdownItem) =>
    child.category === parent.category &&
    (child.groupSubcategory !== undefined
        ? child.groupSubcategory === parent.subcategory
        : !!child.subcategory?.includes(parent.subcategory || ''));

const REFUND_TO_LABELS: Record<string, string> = {
    card: 'Card',
    wallet: 'Wallet',
    'card+wallet': 'Card + wallet'
};

interface CategoryDataRow {
    id: number | string;
    category: string;
    credit: number;
    debit: number;
    tax: number;
    taxReversed: number;
    outstanding: number;
    net: number;
    level: number;
    parentCategory: string;
    parentSubcategory?: string;
    groupSubcategory?: string; // for level-2 rows: the level-1 parent's subcategory (e.g. amenity type)
    hasChildren: boolean;
    expandKey: string;
    isLiability?: boolean;
    isTotal?: boolean;
}

interface DrillDownRow {
    id: string;
    date: string | null;
    memberName: string;
    label: string;
    status?: string;
    paymentMethod?: string;
    sourceLabel?: string;
    credit: number;
    debit: number;
    tax: number;
    /** HST reversed by a refund in the period */
    taxReversed?: number;
    outstanding: number;
    net: number;
    /** Where a refund went */
    refundTo?: 'card' | 'wallet' | 'card+wallet';
    /** The cancellation / refund date behind this row was estimated */
    approximate?: boolean;
}

function getDrillDownParams(row: CategoryDataRow): DrillDownRequest | null {
    if (row.isTotal) return null; // TOTAL spans multiple sources; not drillable

    if (row.expandKey === 'ar_collected') {
        return { source: 'ar_collected', label: 'Accounts Receivable Collected' };
    }
    if (row.expandKey === 'cancelled_transactions') {
        return { source: 'cancelled', label: 'Cancelled Transactions' };
    }
    if (row.expandKey === 'written_off_transactions') {
        return { source: 'written_off', label: 'Written-Off Transactions' };
    }

    if (row.isLiability) {
        if (row.level === 0) {
            return { source: 'liability_all', label: 'Liabilities' };
        }
        if (row.parentSubcategory === 'Wallet Credits') {
            return { source: 'liability_wallet', label: 'Liabilities — Wallet Credits' };
        }
        if (row.parentSubcategory === 'Personal Training') {
            return { source: 'liability_pt_pack', label: 'Liabilities — Personal Training' };
        }
        return null;
    }

    if (row.parentCategory === 'Membership Payments') {
        return {
            source: 'type',
            transactionType: 'membership_payment',
            tierName: row.level === 1 ? row.parentSubcategory : undefined,
            label: row.level === 1 ? `Membership Payments — ${row.parentSubcategory}` : 'Membership Payments'
        };
    }

    if (row.parentCategory === 'Amenity Bookings') {
        if (row.level === 2) {
            return {
                source: 'type',
                transactionType: 'amenity_booking',
                amenityType: row.groupSubcategory,
                amenityName: row.parentSubcategory,
                label: `Amenity Bookings — ${row.groupSubcategory} — ${row.parentSubcategory}`
            };
        }
        if (row.level === 1) {
            return {
                source: 'type',
                transactionType: 'amenity_booking',
                amenityType: row.parentSubcategory,
                label: `Amenity Bookings — ${row.parentSubcategory}`
            };
        }
        return { source: 'type', transactionType: 'amenity_booking', label: 'Amenity Bookings' };
    }

    const posSource = POS_SALES_SOURCES[row.parentCategory];
    if (posSource) {
        if (row.level === 1 && row.parentSubcategory) {
            return {
                source: posSource,
                paymentMethod: POS_METHOD_BY_LABEL[row.parentSubcategory],
                label: `${row.parentCategory} — ${row.parentSubcategory}`
            };
        }
        return { source: posSource, label: row.parentCategory };
    }

    const flatType = FLAT_CATEGORY_TYPE_MAP[row.parentCategory];
    if (flatType) {
        return { source: 'type', transactionType: flatType, label: row.parentCategory };
    }

    return null;
}

export const AccountingDrillDownReport: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [revenueBreakdown, setRevenueBreakdown] = useState<RevenueBreakdownItem[]>([]);

    const now = new Date();
    const [startDate, setStartDate] = useState<Date>(new Date(now.getFullYear(), now.getMonth(), 1));
    const [endDate, setEndDate] = useState<Date>(new Date(now.getFullYear(), now.getMonth() + 1, 0));
    const [arCollected, setArCollected] = useState<number>(0);
    const [reportWarnings, setReportWarnings] = useState<string[]>([]);
    const [cancelledTransactions, setCancelledTransactions] = useState<number>(0);
    const [writtenOffTransactions, setWrittenOffTransactions] = useState<number>(0);
    const [writtenOffTax, setWrittenOffTax] = useState<number>(0);

    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['Membership Payments']));

    // Drill-down state
    const [drillDown, setDrillDown] = useState<DrillDownRequest | null>(null);
    const [drillRows, setDrillRows] = useState<DrillDownRow[]>([]);
    const [drillTotal, setDrillTotal] = useState(0);
    const [drillLoading, setDrillLoading] = useState(false);
    const [drillError, setDrillError] = useState<string | null>(null);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'date', sort: 'desc' }]);

    useEffect(() => {
        loadReportData();
    }, [startDate, endDate]);

    const loadReportData = async () => {
        try {
            setLoading(true);
            setError(null);

            const startDateStr = formatDateForInput(startDate);
            const endDateStr = formatDateForInput(endDate);

            const stats = await reportAPI.getTransactionStats({
                startDate: startDateStr,
                endDate: endDateStr
            });

            setRevenueBreakdown(stats.revenueBreakdown || []);
            setArCollected(stats.arCollected || 0);
            setReportWarnings(stats.warnings || []);
            setCancelledTransactions(stats.cancelledTransactions || 0);
            setWrittenOffTransactions(stats.writtenOffTransactions || 0);
            setWrittenOffTax(stats.writtenOffTax || 0);
        } catch (err: any) {
            console.error('Error loading accounting drill-down report:', err);
            setError(err.message || 'Failed to load report data');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (value: number): string => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(value);
    };

    // Date-only values (YYYY-MM-DD) are formatted without a timezone shift
    const formatDate = (value: string | null): string => (value ? formatDateDisplay(value) : '');

    const toggleCategory = (category: string) => {
        setExpandedCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(category)) {
                newSet.delete(category);
            } else {
                newSet.add(category);
            }
            return newSet;
        });
    };

    // ─── Build the category-level rows (same shape/logic as AccountingReport) ───
    const buildCategoryRows = (): CategoryDataRow[] => {
        const dataRows: CategoryDataRow[] = revenueBreakdown
            .filter(item => {
                const level = item.level ?? (item.isSubcategory ? 1 : 0);
                if (level === 0) return true;
                if (level === 1) return expandedCategories.has(item.category);
                if (level === 2) {
                    const parentLevel1 = revenueBreakdown.find(
                        parent => parent.level === 1 && isChildOf(item, parent)
                    );
                    if (parentLevel1) {
                        const parentKey = `${parentLevel1.category}::${parentLevel1.subcategory}`;
                        return expandedCategories.has(parentKey);
                    }
                    return false;
                }
                return false;
            })
            .map((item, index) => {
                const level = item.level ?? (item.isSubcategory ? 1 : 0);

                let hasChildren = false;
                let groupSubcategory: string | undefined;
                if (level === 0) {
                    hasChildren = revenueBreakdown.some(child => child.level === 1 && child.category === item.category);
                } else if (level === 1) {
                    hasChildren = revenueBreakdown.some(
                        child => child.level === 2 && isChildOf(child, item)
                    );
                } else if (level === 2) {
                    const parentLevel1 = revenueBreakdown.find(
                        parent => parent.level === 1 && isChildOf(item, parent)
                    );
                    groupSubcategory = parentLevel1?.subcategory;
                }

                const c = rowColumns(item);

                return {
                    id: index,
                    category: item.subcategory || item.category,
                    credit: c.credit,
                    debit: c.debit,
                    tax: item.tax,
                    taxReversed: c.taxReversed,
                    outstanding: item.outstanding,
                    net: c.net,
                    level,
                    parentCategory: item.category,
                    parentSubcategory: item.subcategory,
                    groupSubcategory,
                    hasChildren,
                    expandKey: level === 0 ? item.category : `${item.category}::${item.subcategory}`,
                    isLiability: item.isLiability ?? false
                };
            });

        const parentRows = revenueBreakdown.filter(item => {
            const level = item.level ?? (item.isSubcategory ? 1 : 0);
            return level === 0 && !item.isLiability;
        });

        const totalCredit = parentRows.reduce((sum, item) => sum + rowColumns(item).credit, 0);
        const totalDebit = parentRows.reduce((sum, item) => sum + rowColumns(item).debit, 0);
        const totalTax = parentRows.reduce((sum, item) => sum + item.tax, 0);
        const totalTaxReversed = parentRows.reduce((sum, item) => sum + rowColumns(item).taxReversed, 0);
        const totalOutstanding = parentRows.reduce((sum, item) => sum + item.outstanding, 0);
        const totalNet = parentRows.reduce((sum, item) => sum + rowColumns(item).net, 0) + arCollected;

        if (arCollected > 0) {
            dataRows.push({
                id: -1, category: 'Accounts Receivable Collected', credit: 0, debit: 0, tax: 0, taxReversed: 0, outstanding: 0,
                net: arCollected, level: 0, parentCategory: '', parentSubcategory: '', hasChildren: false,
                expandKey: 'ar_collected', isLiability: false
            });
        }
        if (cancelledTransactions > 0) {
            dataRows.push({
                id: -2, category: 'Cancelled Transactions', credit: 0, debit: 0, tax: 0, taxReversed: 0, outstanding: 0,
                net: -cancelledTransactions, level: 0, parentCategory: '', parentSubcategory: '', hasChildren: false,
                expandKey: 'cancelled_transactions', isLiability: false
            });
        }
        if (writtenOffTransactions > 0) {
            dataRows.push({
                id: -3, category: 'Written-Off Transactions', credit: 0, debit: 0, tax: writtenOffTax, taxReversed: 0, outstanding: 0,
                net: -writtenOffTransactions, level: 0, parentCategory: '', parentSubcategory: '', hasChildren: false,
                expandKey: 'written_off_transactions', isLiability: false
            });
        }

        return [
            ...dataRows,
            {
                id: 'total', category: 'TOTAL', credit: totalCredit, debit: totalDebit, tax: totalTax, taxReversed: totalTaxReversed,
                outstanding: totalOutstanding, net: totalNet, level: -1, parentCategory: '', parentSubcategory: '',
                hasChildren: false, expandKey: 'total', isTotal: true
            }
        ];
    };

    const handleExportSummaryCSV = () => {
        const rows = buildCategoryRows().filter(r => r.id !== 'total' || true);
        const headers = ['Category', 'Credit', 'Debit', 'Tax', 'Tax Reversed', 'Outstanding', 'Net'];
        const csvRows = rows.map(item => [
            csvCell(item.category),
            item.credit.toFixed(2),
            item.debit.toFixed(2),
            item.tax.toFixed(2),
            item.taxReversed.toFixed(2),
            item.outstanding.toFixed(2),
            item.net.toFixed(2)
        ]);
        downloadCSV(headers, csvRows, `accounting-drill-down-summary-${formatDateForInput(startDate)}-to-${formatDateForInput(endDate)}.csv`);
    };

    const csvCell = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

    const downloadCSV = (headers: string[], rows: string[][], filename: string) => {
        const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // ─── Drill-down data fetch ───
    const fetchDrillDown = async () => {
        if (!drillDown) return;
        try {
            setDrillLoading(true);
            setDrillError(null);
            const sort = sortModel[0];
            const response = await reportAPI.getTransactionDrillDown({
                startDate: formatDateForInput(startDate),
                endDate: formatDateForInput(endDate),
                source: drillDown.source,
                transactionType: drillDown.transactionType,
                tierName: drillDown.tierName,
                amenityType: drillDown.amenityType,
                amenityName: drillDown.amenityName,
                paymentMethod: drillDown.paymentMethod,
                sortBy: sort?.field,
                sortOrder: sort?.sort || undefined,
                limit: paginationModel.pageSize,
                offset: paginationModel.page * paginationModel.pageSize
            });
            setDrillRows(response.rows || []);
            setDrillTotal(response.total || 0);
        } catch (err: any) {
            console.error('Error loading drill-down transactions:', err);
            setDrillError(err.message || 'Failed to load transactions');
        } finally {
            setDrillLoading(false);
        }
    };

    useEffect(() => {
        if (drillDown) {
            fetchDrillDown();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drillDown, paginationModel, sortModel]);

    const openDrillDown = (row: CategoryDataRow) => {
        const params = getDrillDownParams(row);
        if (!params) return;
        setPaginationModel({ page: 0, pageSize: 25 });
        setSortModel([{ field: 'date', sort: 'desc' }]);
        setDrillDown(params);
    };

    const closeDrillDown = () => {
        setDrillDown(null);
        setDrillRows([]);
        setDrillTotal(0);
        setDrillError(null);
    };

    const handleExportDrillDownCSV = async () => {
        if (!drillDown) return;
        try {
            const sort = sortModel[0];
            const response = await reportAPI.getTransactionDrillDown({
                startDate: formatDateForInput(startDate),
                endDate: formatDateForInput(endDate),
                source: drillDown.source,
                transactionType: drillDown.transactionType,
                tierName: drillDown.tierName,
                amenityType: drillDown.amenityType,
                amenityName: drillDown.amenityName,
                paymentMethod: drillDown.paymentMethod,
                sortBy: sort?.field,
                sortOrder: sort?.sort || undefined,
                limit: 5000,
                offset: 0
            });
            const headers = ['Date', 'Member', 'Type', 'Status', 'Payment Method', 'Credit', 'Debit', 'Tax', 'Tax Reversed', 'Refund To', 'Outstanding', 'Net', 'Estimated Date'];
            const csvRows = (response.rows || []).map((r: DrillDownRow) => [
                csvCell(r.date ?? ''),
                csvCell(r.memberName),
                csvCell(r.label),
                r.status || '',
                csvCell(r.paymentMethod ? (POS_METHOD_LABELS[r.paymentMethod] ?? r.paymentMethod) : ''),
                r.credit.toFixed(2),
                r.debit.toFixed(2),
                r.tax.toFixed(2),
                (r.taxReversed ?? 0).toFixed(2),
                r.refundTo ? REFUND_TO_LABELS[r.refundTo] : '',
                r.outstanding.toFixed(2),
                r.net.toFixed(2),
                r.approximate ? 'yes' : ''
            ]);
            downloadCSV(headers, csvRows, `accounting-drill-down-${drillDown.label.replace(/[^a-z0-9]+/gi, '-')}-${formatDateForInput(startDate)}-to-${formatDateForInput(endDate)}.csv`);
        } catch (err: any) {
            console.error('Error exporting drill-down CSV:', err);
            setDrillError(err.message || 'Failed to export transactions');
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    const drillColumns: GridColDef[] = [
        { field: 'date', headerName: 'Date', flex: 0.6, minWidth: 110, valueFormatter: (params) => formatDate(params as string) },
        { field: 'memberName', headerName: 'Member', flex: 1, minWidth: 160 },
        { field: 'label', headerName: 'Type', flex: 1, minWidth: 160 },
        {
            field: 'status',
            headerName: 'Status',
            flex: 0.6,
            minWidth: 110,
            renderCell: (params) => (
                <Box display="flex" alignItems="center" gap={0.5}>
                    {params.value}
                    {params.row.approximate && (
                        <Tooltip title="The cancellation / refund date for this charge was estimated (it happened before status changes were tracked)." arrow>
                            <InfoOutlined sx={{ fontSize: 15, color: 'warning.main', cursor: 'help' }} />
                        </Tooltip>
                    )}
                </Box>
            )
        },
        {
            field: 'paymentMethod',
            headerName: 'Payment Method',
            flex: 0.7,
            minWidth: 130,
            sortable: false,
            valueFormatter: (value: string | undefined) => (value ? (POS_METHOD_LABELS[value] ?? value) : '')
        },
        { field: 'credit', headerName: 'Credit', flex: 0.6, minWidth: 110, align: 'right', headerAlign: 'right', valueFormatter: (params) => formatCurrency(params as number) },
        { field: 'debit', headerName: 'Debit', flex: 0.6, minWidth: 110, align: 'right', headerAlign: 'right', valueFormatter: (params) => formatCurrency(params as number) },
        { field: 'tax', headerName: 'Tax', flex: 0.6, minWidth: 110, align: 'right', headerAlign: 'right', valueFormatter: (params) => formatCurrency(params as number) },
        { field: 'taxReversed', headerName: 'Tax Reversed', flex: 0.6, minWidth: 110, align: 'right', headerAlign: 'right', valueFormatter: (params) => formatCurrency((params as number) || 0) },
        { field: 'refundTo', headerName: 'Refund To', flex: 0.5, minWidth: 100, sortable: false, valueFormatter: (value: string | undefined) => (value ? REFUND_TO_LABELS[value] ?? value : '') },
        { field: 'outstanding', headerName: 'Outstanding', flex: 0.6, minWidth: 120, align: 'right', headerAlign: 'right', valueFormatter: (params) => formatCurrency(params as number) },
        { field: 'net', headerName: 'Net', flex: 0.6, minWidth: 110, align: 'right', headerAlign: 'right', valueFormatter: (params) => formatCurrency(params as number) }
    ];

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 3 }}>
                <RouterLink style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }} to="/dashboard">
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Dashboard
                </RouterLink>
                <RouterLink style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }} to="/reports">
                    Reports
                </RouterLink>
                {drillDown ? (
                    <MuiLink
                        component="button"
                        onClick={closeDrillDown}
                        style={{ textDecoration: 'none' }}
                    >
                        Accounting - Drill Down Report
                    </MuiLink>
                ) : (
                    <Typography color="text.primary">Accounting - Drill Down Report</Typography>
                )}
                {drillDown && <Typography color="text.primary">{drillDown.label}</Typography>}
            </Breadcrumbs>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom>
                    Accounting - Drill Down Report
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
                {reportWarnings.map((w, i) => <Alert key={i} severity="warning" sx={{ mb: 2 }}>{w}</Alert>)}

                <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <DatePicker
                                label="Start Date"
                                value={startDate}
                                onChange={(newValue) => { if (newValue) { setStartDate(newValue); closeDrillDown(); } }}
                                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <DatePicker
                                label="End Date"
                                value={endDate}
                                onChange={(newValue) => { if (newValue) { setEndDate(newValue); closeDrillDown(); } }}
                                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                            />
                        </Grid>
                    </Grid>
                </Paper>

                {!drillDown ? (
                    <Paper elevation={2} sx={{ p: 3 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6">Revenue Summary</Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Download />}
                                onClick={handleExportSummaryCSV}
                                disabled={!revenueBreakdown || revenueBreakdown.length === 0}
                            >
                                Export CSV
                            </Button>
                        </Box>
                        {revenueBreakdown && revenueBreakdown.length > 0 ? (
                            <DataGrid
                                rows={buildCategoryRows()}
                                columns={[
                                    {
                                        field: 'category',
                                        headerName: 'Category',
                                        flex: 1,
                                        minWidth: 220,
                                        renderCell: (params) => {
                                            const row = params.row as CategoryDataRow;
                                            const drillable = !row.isTotal && !!getDrillDownParams(row);
                                            return (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        pl: row.isTotal ? 0 : row.level * 4,
                                                        fontWeight: row.isTotal ? 'bold' : row.level === 0 ? 'bold' : row.level === 1 ? 600 : 'normal',
                                                        width: '100%',
                                                        borderTop: row.isTotal ? '2px solid' : row.isLiability && row.level === 0 ? '2px solid' : 'none',
                                                        borderColor: row.isTotal ? 'divider' : row.isLiability && row.level === 0 ? 'divider' : 'transparent',
                                                        pt: row.isTotal ? 1 : row.isLiability && row.level === 0 ? 1 : 0,
                                                        color: row.isLiability ? 'warning.dark' : 'inherit'
                                                    }}
                                                >
                                                    {row.hasChildren && (
                                                        <IconButton size="small" onClick={() => toggleCategory(row.expandKey)} sx={{ mr: 1, p: 0 }}>
                                                            {expandedCategories.has(row.expandKey) ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
                                                        </IconButton>
                                                    )}
                                                    {drillable ? (
                                                        <MuiLink component="button" onClick={() => openDrillDown(row)} sx={{ textAlign: 'left' }}>
                                                            {params.value}
                                                        </MuiLink>
                                                    ) : (
                                                        params.value
                                                    )}
                                                    {row.expandKey === 'ar_collected' && (
                                                        <Tooltip title="Prior transactions that were collected during this report period." arrow placement="right">
                                                            <InfoOutlined sx={{ ml: 0.75, fontSize: 15, color: 'text.secondary', cursor: 'help', flexShrink: 0 }} />
                                                        </Tooltip>
                                                    )}
                                                    {row.expandKey === 'cancelled_transactions' && (
                                                        <Tooltip title="Charges billed (and still unpaid) in an earlier period that were cancelled during this period. Reduces accounts receivable." arrow placement="right">
                                                            <InfoOutlined sx={{ ml: 0.75, fontSize: 15, color: 'text.secondary', cursor: 'help', flexShrink: 0 }} />
                                                        </Tooltip>
                                                    )}
                                                    {row.expandKey === 'written_off_transactions' && (
                                                        <Tooltip title="Charges written off during this period, whatever their date. A charge written off in the period it was billed also shows as a sale (Outstanding)." arrow placement="right">
                                                            <InfoOutlined sx={{ ml: 0.75, fontSize: 15, color: 'text.secondary', cursor: 'help', flexShrink: 0 }} />
                                                        </Tooltip>
                                                    )}
                                                    {row.isLiability && row.level === 0 && (
                                                        <Tooltip title="Amounts the club owes to members. Not included in the TOTAL." arrow placement="right">
                                                            <InfoOutlined sx={{ ml: 0.75, fontSize: 15, color: 'text.secondary', cursor: 'help', flexShrink: 0 }} />
                                                        </Tooltip>
                                                    )}
                                                    {row.isLiability && row.parentSubcategory === 'Wallet Credits' && (
                                                        <Tooltip title="Change in member wallet balances this period, gross: credits issued (refunds, admin credits, gift cards) minus wallet used to pay charges." arrow placement="right">
                                                            <InfoOutlined sx={{ ml: 0.75, fontSize: 15, color: 'text.secondary', cursor: 'help', flexShrink: 0 }} />
                                                        </Tooltip>
                                                    )}
                                                    {row.isLiability && row.parentSubcategory === 'F&B Tips (payable to staff)' && (
                                                        <Tooltip
                                                            title="Tips collected on F&B tabs, less any tips refunded with a refunded member-account charge. Owed to staff, not revenue."
                                                            arrow
                                                            placement="right"
                                                        >
                                                            <InfoOutlined sx={{ ml: 0.75, fontSize: 15, color: 'text.secondary', cursor: 'help', flexShrink: 0 }} />
                                                        </Tooltip>
                                                    )}
                                                    {row.isLiability && row.parentSubcategory === 'Personal Training' && (
                                                        <Tooltip
                                                            title="Net is the remaining value of active personal training packs purchased in this period (remaining sessions × cost per session). Tax, however, is the tax collected on ALL personal training pack purchases completed in this period — including packs already fully used or archived by now, which no longer count toward Net. Drill in to see both."
                                                            arrow
                                                            placement="right"
                                                        >
                                                            <InfoOutlined sx={{ ml: 0.75, fontSize: 15, color: 'text.secondary', cursor: 'help', flexShrink: 0 }} />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            );
                                        }
                                    },
                                    { field: 'credit', headerName: 'Credit', flex: 0.7, minWidth: 120, align: 'right', headerAlign: 'right', valueFormatter: (p) => formatCurrency(p as number) },
                                    { field: 'debit', headerName: 'Debit', flex: 0.7, minWidth: 120, align: 'right', headerAlign: 'right', valueFormatter: (p) => formatCurrency(p as number) },
                                    { field: 'tax', headerName: 'Tax', flex: 0.7, minWidth: 120, align: 'right', headerAlign: 'right', valueFormatter: (p) => formatCurrency(p as number) },
                                    { field: 'taxReversed', headerName: 'Tax Reversed', flex: 0.7, minWidth: 120, align: 'right', headerAlign: 'right', valueFormatter: (p) => formatCurrency(p as number) },
                                    { field: 'outstanding', headerName: 'Outstanding', flex: 0.7, minWidth: 120, align: 'right', headerAlign: 'right', valueFormatter: (p) => formatCurrency(p as number) },
                                    { field: 'net', headerName: 'Net', flex: 0.7, minWidth: 120, align: 'right', headerAlign: 'right', valueFormatter: (p) => formatCurrency(p as number) }
                                ] as GridColDef[]}
                                autoHeight
                                hideFooter
                                disableRowSelectionOnClick
                                disableColumnMenu
                                sx={{ border: 'none' }}
                            />
                        ) : (
                            <Typography color="textSecondary" variant="body2" textAlign="center">
                                No revenue breakdown available
                            </Typography>
                        )}
                    </Paper>
                ) : (
                    <Paper elevation={2} sx={{ p: 3 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Button startIcon={<ArrowBackIcon />} onClick={closeDrillDown} size="small">
                                    Back to Categories
                                </Button>
                                <Typography variant="h6" sx={{ ml: 2 }}>{drillDown.label}</Typography>
                            </Box>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Download />}
                                onClick={handleExportDrillDownCSV}
                                disabled={drillTotal === 0}
                            >
                                Export CSV
                            </Button>
                        </Box>

                        {drillError && <Alert severity="error" sx={{ mb: 2 }}>{drillError}</Alert>}

                        <DataGrid
                            rows={drillRows}
                            columns={drillColumns}
                            loading={drillLoading}
                            paginationMode="server"
                            sortingMode="server"
                            rowCount={drillTotal}
                            paginationModel={paginationModel}
                            onPaginationModelChange={setPaginationModel}
                            sortModel={sortModel}
                            onSortModelChange={setSortModel}
                            pageSizeOptions={[10, 25, 50, 100]}
                            autoHeight
                            disableRowSelectionOnClick
                            disableColumnMenu
                            sx={{ border: 'none' }}
                        />
                    </Paper>
                )}
            </Box>
            </LocalizationProvider>
        </Container>
    );
};
